'use client'

import { getImageProps } from 'next/image'
import { css, styled } from 'styled-components'

import { ButtonAsA, FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'

import { useImageLoader } from '@/components/Image'

import { LINKS } from './content'
import { Inner, Section } from './ui'
import { useAuthCtaTracking } from './useAuthCtaTracking'

const belowVisualOnPhone = css`
  @media ${Breakpoints.max.small} {
    order: 1;
  }
`

const HeroInner = styled(Inner)`
  gap: 2rem;

  @media ${Breakpoints.max.small} {
    gap: 1rem;
  }

  @media ${Breakpoints.min.large} {
    flex-direction: row;
    align-items: center;
    gap: 4rem;
  }
`

const Copy = styled.div`
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 1rem;

  @media ${Breakpoints.max.small} {
    display: contents;
  }
`

const Eyebrow = styled.p`
  ${Typography.Subtitle2};
  margin: 0;
  color: ${Colors.Semantic.BStock.onhover};
  text-transform: uppercase;
  letter-spacing: 0.07rem;
`

const Title = styled.h1`
  ${Typography.Title1};
  margin: 0;
  color: ${Colors.Semantic.Neutral.light_black};
`

const Body = styled.p`
  ${Typography.Body1};
  margin: 0;
  text-wrap: pretty;
  color: ${Colors.Semantic.Neutral.dark_grey};
  ${belowVisualOnPhone};
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;

  @media ${Breakpoints.max.small} {
    flex-direction: column;
    order: 1;
  }
`

const Visual = styled.picture`
  display: block;
  width: 100%;
  max-width: 40rem;
  min-width: 0;

  @media ${Breakpoints.max.small} {
    margin-block: 0.5rem;
  }

  @media ${Breakpoints.min.large} {
    flex: 1 1 0;
    max-width: none;
  }
`

const VisualImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 700 / 400;

  @media ${Breakpoints.min.medium} {
    aspect-ratio: 1280 / 720;
  }
`

export default function Hero() {
  const { onCreateAccount } = useAuthCtaTracking('homepage_hero')
  const loader = useImageLoader()
  const shared = {
    alt: '',
    loader,
    loading: 'eager',
    fetchPriority: 'high',
  } as const
  const { props: desktop } = getImageProps({
    ...shared,
    src: '/home-portal/images/loggedOutHomepage/hero-desktop.webp',
    width: 1280,
    height: 720,
    sizes:
      '(min-width: 90rem) 640px, (min-width: 64rem) calc(50vw - 5rem), 40rem',
  })
  const { props: phoneProps } = getImageProps({
    ...shared,
    src: '/home-portal/images/loggedOutHomepage/hero-phone.webp',
    width: 700,
    height: 400,
    sizes: 'calc(100vw - 3rem)',
  })

  return (
    <Section data-testid="logged-out-hero">
      <HeroInner>
        <Copy>
          <Eyebrow>
            <FormattedMessage id="LoggedOutHomepage.hero.eyebrow" />
          </Eyebrow>
          <Title>
            <FormattedMessage id="LoggedOutHomepage.hero.title" />
          </Title>
          <Body>
            <FormattedMessage id="LoggedOutHomepage.hero.body" />
          </Body>
          <Actions>
            <ButtonAsA
              appearance="primary"
              size="large"
              href={LINKS.signup}
              onClick={onCreateAccount}
            >
              <FormattedMessage id="LoggedOutHomepage.hero.createAccount" />
            </ButtonAsA>
            <ButtonAsA
              appearance="secondary"
              size="large"
              href={LINKS.allAuctions}
            >
              <FormattedMessage id="LoggedOutHomepage.hero.browseListings" />
            </ButtonAsA>
          </Actions>
        </Copy>
        <Visual>
          <source
            media={Breakpoints.min.medium}
            srcSet={desktop.srcSet}
            sizes={desktop.sizes}
            width={desktop.width}
            height={desktop.height}
          />
          <VisualImage {...phoneProps} />
        </Visual>
      </HeroInner>
    </Section>
  )
}
